import { useEffect, useRef, useState } from 'react';

import { Alert, Button, Card, Heading, Spinner, Stack, Text } from '../design/index.js';
import { acceptInviteByToken } from '../api.js';
import type { InviteSummary } from '../api.js';
import styles from './InviteAccept.module.css';

export interface InviteAcceptProps {
  token: string;
  /** Fired once the invitation has been accepted, so the app can re-read
   *  itself and show the dashboard with the new person available. */
  onAccepted: () => void;
  /** Leaves the invitation alone and continues to the app. */
  onDismiss: () => void;
  /**
   * Fired as soon as the server has answered, whichever way it answered.
   *
   * Separate from `onAccepted` on purpose: that one means "take me to the
   * dashboard now", which happens when the reader clicks. This one means
   * "this token is spent", which happens earlier, and the two are not the
   * same moment — a refresh in between used to replay a settled invitation.
   */
  onSettled: () => void;
}

type Phase =
  | { kind: 'working' }
  | { kind: 'accepted'; invite: InviteSummary }
  | { kind: 'failed'; message: string };

/**
 * Lands a signed-in visitor on the invitation they arrived holding, and
 * accepts it — see `App.tsx` for how the token gets here across the Google
 * round trip.
 *
 * The accept call fires exactly once. A `Phase` of `'working'` is not
 * enough of a guard on its own: React StrictMode mounts this component,
 * runs its effects, tears it down, and mounts it again in development, and
 * by the second mount the request from the first has often already landed
 * server-side — so a naive "only call while phase is working" check would
 * still double-fire. A ref survives that unmount/remount because it is not
 * component state; it is set synchronously before the request goes out, so
 * the second effect run sees it already true and skips the call entirely.
 */
export function InviteAccept({ token, onAccepted, onDismiss, onSettled }: InviteAcceptProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'working' });
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;

    let cancelled = false;
    acceptInviteByToken(token)
      .then((invite) => {
        // Retire the token the instant the server has acted on it, not when
        // the reader gets round to clicking Continue. Leaving it live in the
        // meantime meant a refresh on this very screen re-sent an invitation
        // that had already been accepted — which the server rightly refuses,
        // so a share that had genuinely worked was reported as a dead link.
        onSettled();
        if (!cancelled) setPhase({ kind: 'accepted', invite });
      })
      .catch(() => {
        // Spent either way: a token the server refused is not going to start
        // working, and keeping it would replay this same failure on reload.
        onSettled();
        if (cancelled) return;
        // Expired, already-used and unknown tokens all come back as a 404
        // from the server (see the wave header) — there is nothing more
        // specific to tell them apart by, and a status code would not mean
        // anything to whoever is reading this.
        setPhase({
          kind: 'failed',
          message: 'This invitation link is no longer valid — ask them to send a new one.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [token, onSettled]);

  return (
    <main className={styles.page}>
      <Card className={styles.card} variant="raised" data-testid="invite-accept">
        {phase.kind === 'working' && (
          <Stack gap={4} align="center" data-testid="invite-accept-working">
            <Spinner aria-label="Accepting the invitation" />
            <Text color="secondary">Accepting your invitation…</Text>
          </Stack>
        )}

        {phase.kind === 'accepted' && (
          <Stack gap={5} data-testid="invite-accept-success">
            <Stack gap={2}>
              <Heading level={2} size="lg">You&rsquo;re in</Heading>
              <Text color="secondary">
                {inviteAcceptedCopy(phase.invite)}
              </Text>
            </Stack>
            <Button onClick={onAccepted} data-testid="invite-accept-continue">
              Go to your dashboard
            </Button>
          </Stack>
        )}

        {phase.kind === 'failed' && (
          <Stack gap={5} data-testid="invite-accept-failed">
            <Alert variant="error" title="Couldn't accept this invitation">
              {phase.message}
            </Alert>
            <Button onClick={onDismiss} data-testid="invite-accept-dismiss">
              Continue to log.gy
            </Button>
          </Stack>
        )}
      </Card>
    </main>
  );
}

function inviteAcceptedCopy(invite: InviteSummary): string {
  const names = invite.nodes.map((node) => node.title);
  const what =
    names.length === 0
      ? 'their dashboard'
      : names.length === 1
        ? names[0]
        : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${invite.fromDisplayName} shared ${what} with you. It now shows up wherever you switch who you're viewing.`;
}
